import { saveAs } from "file-saver";
import { isEmpty, isEqual } from "lodash";
import { useSelector } from "react-redux";
import { useCurrentWidgetState, usePatchedWidgetComponentProps, useWidgetCurrentDisplayOptions, useWidgetCurrentGridSwitch } from "../hooks/use-current-widget";
import { getDatapointGridStateId } from "./common";
import { createDatapointTotalGroupExcelOptions, tryToAddCustomDatapointsAsGroups } from "./excelExportOptionsHelper";
import { processCellForExportSelector } from "./export-handlers/process-cell-for-export-selector";
import { InsGridAggs } from "./grid-agg-funcs";
import { postDatapointsExcel, DatapointExcelOptionColumnSortEnum, postDatapointsExcelComparison, DatapointIdStandardEnum, CommitmentCalculation } from "api/ux/src";
import { InspectDataComparisonTab, InspectDataTab } from "components/modal/widget-inspect-data/types";
import { useGetSelectedDealFundId } from "components/workspaces/hooks/use-current-asset-dashboard";
import { useCurrentDashboardOptions } from "components/workspaces/hooks/use-current-dashboard";
import { useGetEntityInfo } from "components/workspaces/hooks/use-get-dashboards-by-type";
import { getConnectionId } from "hooks/use-signal-r";
import { ReferenceDateDimension } from "hooks/widget-data/datapoints-api/types";
import { createId, useCreateWidgetRequest, useGetDatapointsWidgetParams, useGetEntityComparates } from "hooks/widget-data/use-datapoints";
import { language } from "language/default";
import { WidgetMode, componentPropsPatcher } from "store/current-dashboard/types";
import { useGetGridGroups, useHasGridColumnState } from "store/grids";
export const useGetExcelExportOptions = (sectionId, widgetId) => {
    const dataScale = useCurrentWidgetState(sectionId, widgetId)?.dataScale ?? "";
    return { columnGroups: true,
        processCellCallback: (params) => {
            processCellForExportSelector({ dataScale, ...params });
        } };
};
const withOptions = (exportExcelQueryConfig, sync = false) => {
    exportExcelQueryConfig.force = true;
    exportExcelQueryConfig.options = exportExcelQueryConfig.options || {};
    exportExcelQueryConfig.options.headers = exportExcelQueryConfig.options.headers || {};
    exportExcelQueryConfig.options.headers = { ...exportExcelQueryConfig.options.headers, connectionId: getConnectionId("raw-template-hub"), "use-async": "true" };
    if (sync) {
        exportExcelQueryConfig.options.headers.requestBlob = "true";
        exportExcelQueryConfig.meta = { ...{ errorMessage: language.en.grid.excelExport.downloadExcelFailed,
                pendingMessage: language.en.grid.excelExport.downloadExcelPending,
                successMessage: language.en.grid.excelExport.downloadExcelSucceeded } };
    }
    return exportExcelQueryConfig;
};
const getExcelFileName = (entityName, request, isComparison = false) => {
    const asOfDate = request?.asOfDate?.toLocaleDateString().replaceAll("/", "-");
    return `Insight${isComparison ? "Comparison" : ""}Export_${request?.entityType}_${entityName || request?.entityId}_AsOf_${asOfDate}.xlsx`;
};
const getColumnSort = (sort) => {
    switch (sort) {
        case "asc": return DatapointExcelOptionColumnSortEnum.Asc;
        case "desc": return DatapointExcelOptionColumnSortEnum.Desc;
        default: return null;
    }
};
const virtualMeasures = [ReferenceDateDimension];
const columnDefFieldBaseName = (field) => field?.toLowerCase().split(".")[0] || "";
const shouldBeAddedAsExcelOption = (def, measures) => {
    // Skip columns marked for Excel export exclusion
    if (def?.skipExcelExport) {
        return false;
    }
    const measureNames = measures?.map((m) => columnDefFieldBaseName(m.name)) || [], virtualAndRegularMeasureNames = measureNames.concat(virtualMeasures.map(columnDefFieldBaseName));
    if (def && def.field) {
        const isCustom = measures?.find((x) => x.name === def.field && x.datapoint.custom !== null && x.datapoint.custom !== undefined);
        if (isCustom || virtualAndRegularMeasureNames.includes(columnDefFieldBaseName(def.field))) {
            return true;
        }
    }
    return false;
};
// If inspectIncludeHidden is true don't hide it in Excel, useful when using InsChartSelectorTableFilter
const isColumnHidden = (def) => (def.inspectIncludeHidden ? false : def.hide);
export const getColumnExcelOptions = (columnDefs, measures) => {
    const excelOptions = [];
    const getExcelOptions = (columns, parentName) => {
        if (columns) {
            columns.forEach((columnDef) => {
                const def = columnDef, groupDef = columnDef;
                if (shouldBeAddedAsExcelOption(def, measures)) {
                    const option = { columnGroupName: parentName,
                        columnIsTotalable: def.aggFunc === InsGridAggs.totalData,
                        columnLabel: def.headerName,
                        datapointName: def.field,
                        hide: isColumnHidden(def) };
                    const sort = getColumnSort(def.sort || "");
                    if (sort) {
                        option.columnSort = sort;
                        option.columnSortIndex = def.sortIndex ?? undefined;
                    }
                    excelOptions.push(option);
                }
                if (groupDef && groupDef.children && groupDef.children.length) {
                    getExcelOptions(groupDef.children, groupDef.headerName);
                }
            });
        }
    };
    getExcelOptions(columnDefs);
    return excelOptions;
};
export const getDatapointTotalGroupExcelOptions = (userSelectedGroups, measures, dimensions) => {
    const ret = createDatapointTotalGroupExcelOptions(userSelectedGroups, measures, dimensions);
    tryToAddCustomDatapointsAsGroups(userSelectedGroups, measures, ret);
    return ret;
};
export const getDatapointsRequestExcelRequestItems = (requests, userSelectedGroups, columnDefs, showTotals, sheetName) => {
    const excelRequestItems = [];
    if (!columnDefs?.length || !requests?.length) {
        return excelRequestItems;
    }
    for (const requestItem of requests) {
        const filteredMeasures = (requestItem.measures || []).filter((m) => {
            const n = m.name;
            return !(n?.startsWith("clickthrough-") ||
                n === "fundMasterId" ||
                n === "assetMasterId");
        });
        const datapointExcelOptions = getColumnExcelOptions(columnDefs, filteredMeasures.concat(requestItem?.dimensions || []));
        const excelRequestItem = {
            ...requestItem,
            datapointExcelOptions,
            includeTotalExcelValues: showTotals,
            measures: filteredMeasures,
            sheetName,
            totalGroupExcelOptions: filteredMeasures.length && requestItem.dimensions?.length
                ? getDatapointTotalGroupExcelOptions(userSelectedGroups || [], filteredMeasures, requestItem.dimensions)
                : []
        };
        excelRequestItems.push(excelRequestItem);
    }
    return excelRequestItems;
};
const blobTransform = (response, requestBasedExportFileName) => {
    if (response?.size > 0) {
        // No need to sanitize filename as FileSaver.js does it
        saveAs(response, requestBasedExportFileName);
    }
    return {};
};
export const getDatapointsExcelComparisonRequestQueryConfig = (comparisonRequest, excelRequestItems, entityName, isOnlyDifferenceExport) => {
    const comparisonBaseRequest = comparisonRequest?.datapointsComparisonRequest;
    if (!comparisonBaseRequest) {
        return {};
    }
    let excelComparisonRequestItems = [];
    for (const { request, colDefs, userSelectedGroups, sheetName, showTotals } of excelRequestItems) {
        if ((colDefs?.length || 0) > 0 && (request?.datapointsRequest?.requests?.length || 0) > 0) {
            excelComparisonRequestItems = excelComparisonRequestItems.concat(getDatapointsRequestExcelRequestItems(request?.datapointsRequest?.requests, userSelectedGroups, colDefs, showTotals, sheetName));
        }
    }
    excelComparisonRequestItems.forEach((request) => {
        if (request.measures?.findIndex((x) => x.datapoint?.standard === DatapointIdStandardEnum.PortfolioPortfolioName) === -1) {
            request.measures?.push({ datapoint: { standard: DatapointIdStandardEnum.PortfolioPortfolioName }, name: "PortfolioName" });
        }
    });
    const exportExcelQueryConfig = postDatapointsExcelComparison({ datapointsComparisonExcelRequest: {
            ...comparisonBaseRequest,
            differenceSheetOnly: isOnlyDifferenceExport,
            entityName,
            requests: excelComparisonRequestItems
        } }, {
        transform: () => ({})
    });
    return withOptions(exportExcelQueryConfig);
};
export const getDatapointsExcelRequestQueryConfigAndRequest = (requests, entityName, entitySubName) => {
    const [baseRequest] = requests || [];
    if (!requests?.length || !baseRequest?.colDefs?.length || !baseRequest?.request?.datapointsRequest?.requests?.length) {
        return [{}, {}];
    }
    const requestBasedExportFileName = getExcelFileName(entityName || "", baseRequest?.request?.datapointsRequest);
    let excelRequestItems = [];
    for (const { request, colDefs, userSelectedGroups, sheetName, showTotals } of requests) {
        if ((colDefs?.length || 0) > 0 && (request?.datapointsRequest?.requests?.length || 0) > 0) {
            excelRequestItems = excelRequestItems.concat(getDatapointsRequestExcelRequestItems(request?.datapointsRequest?.requests, userSelectedGroups, colDefs, showTotals, sheetName));
        }
    }
    const datapointsExcelRequest = {
        ...baseRequest.request.datapointsRequest,
        allocationKey: baseRequest.request.datapointsRequest.allocationKey,
        commitmentCalculation: baseRequest.request.datapointsRequest.commitmentCalculation ?? CommitmentCalculation.RecomputedCommitment,
        entityName,
        entitySubName,
        requests: excelRequestItems
    };
    const exportExcelQueryConfig = postDatapointsExcel({ datapointsExcelRequest }, { transform: (body) => blobTransform(body, requestBasedExportFileName) });
    return [withOptions(exportExcelQueryConfig, true), datapointsExcelRequest];
};
export const getDatapointsExcelRequestQueryConfig = (requests, entityName, entitySubName) => {
    const [query] = getDatapointsExcelRequestQueryConfigAndRequest(requests, entityName, entitySubName);
    return query;
};
export const useGetSubEntityName = () => {
    const options = useCurrentDashboardOptions();
    const selectedFundId = useGetSelectedDealFundId();
    const deals = useSelector((state) => state.entities.Asset[state.currentDashboard.currentEntityId]?.deals) || [];
    const subscriptions = useSelector((state) => state.entities.Fund[state.currentDashboard.currentEntityId]?.subscriptions) || [];
    if (selectedFundId && deals?.length) {
        const [fund] = deals.filter((d) => d.fundId === selectedFundId);
        return fund?.fundName;
    }
    if (options?.selectedInvestorId && options?.enabled && subscriptions?.length) {
        const [investor] = subscriptions.filter((d) => d.investmentId === options.selectedInvestorId);
        return investor?.investorName;
    }
    return "";
};
export const useGetSelectedTab = () => useSelector((state) => state.inspectData.inspectDataTab, isEqual);
export const useGetPortfolioName = (pflId) => useSelector((state) => state.entities.Portfolios[pflId]?.name, isEqual);
export const modifyDatapointRequestForComparatePortfolio = (request, comparates) => {
    const requestOutput = { datapointsRequest: { ...request.datapointsRequest, entityId: comparates[0] } };
    createId(requestOutput.datapointsRequest);
    return requestOutput;
};
export const getQueryAndDatapointsExcelRequest = (params) => {
    const { selectedTab, request, showTotals, userSelectedGroups, entityInfo, compProps, comparatePortfolioName, comparates, subEntityName, switched } = params;
    const { gridSwitcher, columnsTemplate } = compProps || {}, usedColumnsTemplate = switched && gridSwitcher?.columnsTemplate ? gridSwitcher?.columnsTemplate : columnsTemplate;
    if (selectedTab === InspectDataTab.Portfolio) {
        const compRequest = request;
        const query = getDatapointsExcelComparisonRequestQueryConfig(compRequest, [
            {
                colDefs: usedColumnsTemplate?.columns,
                request: { datapointsRequest: { ...compRequest.datapointsComparisonRequest } },
                showTotals,
                userSelectedGroups
            }
        ], entityInfo.name, true);
        return [query, {}];
    }
    let actualRowGroups = userSelectedGroups;
    if (!userSelectedGroups?.length && compProps.componentPropsPatchers?.some((p) => p.patcher === componentPropsPatcher.replaceDimensionsWithSelectedGroupByColumns)) {
        actualRowGroups = usedColumnsTemplate?.columns.map((c) => c).filter((c) => c && c.rowGroup && !c.hide && c.field)
            .map((c) => c.field ?? "");
    }
    const { finalName, finalRequest } = selectedTab === InspectDataComparisonTab.ComparatePortfolio
        ? { finalName: comparatePortfolioName, finalRequest: modifyDatapointRequestForComparatePortfolio(request, comparates) }
        : { finalName: entityInfo.name, finalRequest: request };
    const [query, datapointExcelRequest] = getDatapointsExcelRequestQueryConfigAndRequest([
        {
            colDefs: usedColumnsTemplate?.columns || [],
            request: finalRequest,
            showTotals,
            userSelectedGroups: actualRowGroups
        }
    ], finalName, subEntityName);
    return [query, datapointExcelRequest];
};
export const checkRequestAssetConfig = (assetRequest, hasLightPortfolioAnalytics = false) => {
    // Light Portfolio Analytics users cannot export asset cashflows as they do not have access to assets
    if (hasLightPortfolioAnalytics) {
        return false;
    }
    if (assetRequest === null || assetRequest === undefined || isEmpty(assetRequest.datapointsRequest) || isEmpty(assetRequest.datapointsRequest.requests)) {
        return false;
    }
    return true;
};
export const checkRequestInvestConfig = (investRequest) => {
    if (investRequest === null || investRequest === undefined || isEmpty(investRequest.datapointsRequest) || isEmpty(investRequest.datapointsRequest.requests)) {
        return false;
    }
    return true;
};
export const getQueryFromRequest = (paramsNeededForQuery) => {
    const [query] = getQueryAndDatapointsExcelRequest(paramsNeededForQuery);
    return query;
};
export const useGetDatapointsExcelRequest = (paramsNeededForQuery) => {
    const [, datapointExcelRequest] = getQueryAndDatapointsExcelRequest(paramsNeededForQuery);
    return datapointExcelRequest;
};
export const useGetAllNecessaryHooks = (sectionId, widgetId, colDefs) => {
    const compProps = usePatchedWidgetComponentProps(sectionId, widgetId), defaultGroups = colDefs?.filter((e) => e.rowGroup).map((e) => e.field), entityInfo = useGetEntityInfo(), gridStateId = getDatapointGridStateId(sectionId || "", widgetId || ""), params = useGetDatapointsWidgetParams({ sectionId, widgetId }, compProps), selectedTab = useGetSelectedTab(), subEntityName = useGetSubEntityName(), switched = useWidgetCurrentGridSwitch(sectionId, widgetId), userSelectedGroupsState = useGetGridGroups(getDatapointGridStateId(sectionId || "", widgetId || "")), { showTotals } = useWidgetCurrentDisplayOptions(sectionId, widgetId) ?? {};
    const hasColumnState = useHasGridColumnState(gridStateId);
    const userSelectedGroups = hasColumnState ? userSelectedGroupsState : defaultGroups ?? [];
    const request = useCreateWidgetRequest(params, selectedTab === InspectDataTab.Portfolio ? WidgetMode.Comparison : WidgetMode.Normal);
    const comparates = useGetEntityComparates(selectedTab === InspectDataComparisonTab.ComparatePortfolio);
    const comparatePortfolioName = useGetPortfolioName(comparates[0]);
    const paramsNeededForQuery = { compProps, comparatePortfolioName, comparates, entityInfo, request, selectedTab, showTotals, subEntityName, switched, userSelectedGroups };
    return paramsNeededForQuery;
};
export const useGetDatapointsExcelRequestQueryConfig = (sectionId, widgetId, colDefs) => {
    const getNecessaryParams = useGetAllNecessaryHooks(sectionId, widgetId, colDefs);
    const [query] = getQueryAndDatapointsExcelRequest(getNecessaryParams);
    return query;
};
